import React, { useEffect, useState } from 'react';
import BunkerRegistrationList from './BunkerRegistrationList';
import { BunkerRegPageLeftWrapper, BunkerRegistrationContainerStyle } from '../Bunkering.styles';
import BunkerRegistration from './BunkerRegistration';
import { useBunkerContext } from 'src/context/BunkerContext';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { mapBunkering } from 'src/factory/Bunkering/BunkeringFactory';
import { useAuthStore } from 'src/context/AuthContext';
import { InputChangeData, InputType } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { BioFuelType } from 'src/types/ViewModel/Bunkering/BioFuelType';
import { BunkeringEvent } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { FuelType } from 'src/types/ViewModel/Bunkering/FuelType';
import { BIO_DEFAULT_DENSITY, getBunkeringForBunkeringEvent, getBunkeringReference } from '../BunkeringUtils';
import DeletedBunkeringDetails from './DeletedBunkeringDetails';
import { BUNKERING_INPUT_FIELD_KEYS } from 'src/enums/bunkering/bunkeringInputFieldKeys';

const BunkeringRegistrationContainer: React.FC = () => {
    const {
        bunkeringEvents,
        bunkerings,
        selectedBunkeringEventId,
        selectedBunkering,
        setSelectedBunkeringEventId,
        setSelectedBunkeringEvent,
        setSelectedBunkering,
        setSaveButtonEnabled,
        saveBunkeringPageData,
        setSaveBunkeringPageData,
    } = useBunkerContext();
    const { loggedInUser } = useAuthStore();
    const { upsertBunkering } = useGetBunkeringData();

    const [selectedDeletedBunkering, setSelectedDeletedBunkering] = useState<string>(null);

    useEffect(() => {
        if (selectedBunkeringEventId) {
            const bunkeringEvent = bunkeringEvents.find((bunkeringEvent) => bunkeringEvent.id === selectedBunkeringEventId);
            if (!bunkeringEvent) {
                setSelectedBunkeringEvent(null);
                return;
            }
            let bunkering = getBunkeringForBunkeringEvent(bunkeringEvent, bunkerings);
            if (bunkering) {
                bunkering.bunkeringEvent = bunkeringEvent;
            } else {
                bunkering = {
                    bunkeringId: 0,
                    bunkeringGuid: window.crypto.randomUUID(),
                    imoNumber: bunkeringEvent.imoNumber,
                    eventId: bunkeringEvent.eventID,
                    fuelType: bunkeringEvent.fuelType,
                    bunkeringReference: bunkeringEvent.bunkeringReference,
                    bunkeringDateTime: bunkeringEvent.bunkeringDateTimeLocal,
                    bunkerReceivedPerTanks: [],
                    bunkeringStatuses: [],
                    currentStatus: null,
                    bunkeringEvent: bunkeringEvent,
                    proofOfSustainability: false,
                    waterContent: null,
                } as Bunkering;
            }
            setSelectedBunkering(bunkering);
            setSelectedBunkeringEvent(bunkeringEvent);
        } else {
            setSelectedBunkeringEvent(null);
            setSelectedBunkering(null);
        }
        setSaveButtonEnabled(!!selectedBunkeringEventId);
    }, [selectedBunkeringEventId, bunkeringEvents, bunkerings]);

    useEffect(() => {
        if (saveBunkeringPageData) {
            setSaveBunkeringPageData(false);
            handleSaveBunkering();
        }
    }, [saveBunkeringPageData]);

    const handleSaveBunkering = () => {
        selectedBunkering.lastUpdatedAt = new Date();
        selectedBunkering.lastUpdatedBy = loggedInUser.userName ?? loggedInUser.appAccessToken;
        const bunkeringBM = mapBunkering(selectedBunkering);
        upsertBunkering(selectedBunkering.imoNumber, bunkeringBM);
    };

    const updateBunkerReceivedPerTanks = (bunkerReceivedPerTanks) => {
        if (selectedBunkering) {
            const updatedBunkering = { ...selectedBunkering, bunkerReceivedPerTanks };
            setSelectedBunkering(updatedBunkering);
        }
    };

    const handleInputWithChangeObject = (changeObject: InputChangeData, inputType: InputType) => {
        if (selectedBunkering) {
            let val;
            if (inputType === 'number') val = !isNaN(changeObject.event.target.value) ? parseFloat(changeObject.event.target.value) : null;
            else if (inputType === 'checkbox') val = changeObject.event.target.checked;
            else if (inputType === 'select' && changeObject.event.target.name === BUNKERING_INPUT_FIELD_KEYS.BIO_FUEL_TYPE) {
                val = changeObject.event.target.value in BioFuelType ? changeObject.event.target.value : null;
            } else if (inputType === 'select' && changeObject.event.target.name === BUNKERING_INPUT_FIELD_KEYS.FUEL_TYPE) {
                val = changeObject.event.target.value in FuelType ? FuelType[changeObject.event.target.value] : null;
                selectedBunkering.bunkeringReference = getBunkeringReference(
                    FuelType[val],
                    selectedBunkering.bunkeringEvent.portUnCode,
                    selectedBunkering.bunkeringEvent.portName,
                    selectedBunkering.bunkeringDateTime,
                    parseInt(selectedBunkering.bunkeringReference.substring(selectedBunkering.bunkeringReference.length - 1))
                );
            } else val = changeObject.event.target.value;

            if (
                changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS ||
                changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_VOLUME
            ) {
                const unit = changeObject.extraArgs;
                if (unit === 'm3') {
                    selectedBunkering.biofuelMass = val ? Number((val * BIO_DEFAULT_DENSITY).toFixed(2)) : null;
                }
            }

            // If biofuelMass or bioEnergyContent is changed, calculate euLowerCalorificValue
            if (
                changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS ||
                changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_ENERGY_CONTENT ||
                changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_VOLUME
            ) {
                let biofuelMass = selectedBunkering.biofuelMass;
                let biofuelEnergyContent = selectedBunkering.biofuelEnergyContent;
                if (changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_MASS) biofuelMass = val;
                if (changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.BIOFUEL_ENERGY_CONTENT) biofuelEnergyContent = val;

                let lcv: number | null = null;
                if (biofuelMass && biofuelEnergyContent && biofuelEnergyContent > 0 && biofuelMass > 0) {
                    lcv = Number((biofuelEnergyContent / (biofuelMass * 1000)).toFixed(2));
                }
                selectedBunkering.euLowerCalorificValue = lcv;

                // Copy EU LCV to IMO LCV (to be refactored in the future)
                selectedBunkering.lowerHeatingValue = lcv;
            }

            // Copy EU GHG value to IMO value (to be refactored in the future)
            if (changeObject.inputId === BUNKERING_INPUT_FIELD_KEYS.EU_GHG_EMISSIONS) {
                selectedBunkering.imoGhgEmissions = val;
            }

            const updatedBunkering = { ...selectedBunkering, [changeObject.event.target.name]: val };
            setSelectedBunkering(updatedBunkering);
        }
    };

    const removeDebunkering = (debunkeringEvent: BunkeringEvent) => {
        if (selectedBunkering) {
            selectedBunkering.debunkeringEventIds = selectedBunkering.debunkeringEventIds.filter((id) => id !== debunkeringEvent.eventID);
            selectedBunkering.bunkerReceivedPerTanks = selectedBunkering.bunkerReceivedPerTanks.filter(
                (br) => br.debunkeringEventId !== debunkeringEvent.eventID
            );
            const bunkeringBM = mapBunkering(selectedBunkering);
            upsertBunkering(selectedBunkering.imoNumber, bunkeringBM);
        }
    };

    return (
        <BunkerRegistrationContainerStyle>
            <BunkerRegPageLeftWrapper>
                <BunkerRegistrationList
                    selectedBunkeringEventId={selectedBunkeringEventId}
                    selectedDeletedBunkering={selectedDeletedBunkering}
                    setSelectedBunkeringEvent={setSelectedBunkeringEventId}
                    setSelectedDeletedBunkering={setSelectedDeletedBunkering}
                />
            </BunkerRegPageLeftWrapper>

            {selectedDeletedBunkering ? (
                <DeletedBunkeringDetails deletedBunkeringId={selectedDeletedBunkering} onClose={() => setSelectedDeletedBunkering(null)} />
            ) : (
                <BunkerRegistration
                    onSaveSelectedBunkering={handleSaveBunkering}
                    handleInputWithChangeObject={handleInputWithChangeObject}
                    updateBunkerReceivedPerTanks={updateBunkerReceivedPerTanks}
                    removeDebunkering={removeDebunkering}
                />
            )}
        </BunkerRegistrationContainerStyle>
    );
};

export default BunkeringRegistrationContainer;
